import { useTranslation } from '@conekta/cronos/i18n';
import { useContext, type FC, type PropsWithChildren } from 'react';
import { ComponentSize } from '@conekta/frontino-ui/common/constants';
import { Icon } from '@conekta/frontino-ui/Icon';

import 'app/pages/hosted-checkout/components/OrderDetail/styles.scss';
import { IconType } from '@conekta/frontino-ui/Icon/Icon';
import { CheckoutFrameContext } from 'app/context';
import { isLight } from 'app/util/colorHelper';
import { apiAmountToCurrencyString, currencyFormat } from 'app/util/currency';
import { themeInitialValue, themes } from 'app/util/theme';
import { OrderTemplate } from 'common/interface';

import AdditionalChargesSection from '../AdditionalChargesSection';
import ProductRow from '../ProductRow';
import SubscriptionDetail from '../SubscriptionDetail/SubscriptionDetail';
import TotalRow from '../TotalRow';

const OrderDetail: FC<PropsWithChildren<OrderDetailProps>> = ({
  name: orderDetailName,
  amount,
  orderTemplate,
  children,
  hasSubscription,
}) => {
  const { configuration } = useContext(CheckoutFrameContext);
  const { styles = themeInitialValue, sidebar: sidebarStyles } = configuration?.customStyleJSON ?? {
    styles: themeInitialValue,
    sidebar: {},
  };

  const isBackgroundLight = isLight(sidebarStyles?.backgroundColor || themes.dark);
  const colorPrimary = styles?.colors?.primary || themeInitialValue.colors.primary;
  const { t } = useTranslation();
  const parseCurrency = (amountRaw: number) =>
    currencyFormat(orderTemplate.currency, apiAmountToCurrencyString(amountRaw));
  const canShowAdditionalLines =
    orderTemplate.shippingLines.length + orderTemplate.taxLines.length + orderTemplate.discountLines.length > 0;

  const translatedLabels = {
    title: t('hostedCheckout.checkout.shoppingCart'),
    reason: t('hostedCheckout.checkout.concept'),
    product: t('hostedCheckout.checkout.product'),
    price: t('hostedCheckout.checkout.price'),
    quantity: t('hostedCheckout.checkout.quantity'),
    total: t('hostedCheckout.checkout.total'),
  };

  const additionalChargesLabels = {
    subtotal: t('hostedCheckout.checkout.subtotal'),
    shipping: t('hostedCheckout.checkout.shipping'),
    tax: t('hostedCheckout.checkout.tax'),
    discount: t('hostedCheckout.checkout.discount'),
  };

  if (hasSubscription) {
    return (
      <div className="order-detail__subscription">
        {children}
        <SubscriptionDetail />
      </div>
    );
  }

  return (
    <div className="order-detail">
      {children}
      <div className="order-detail__title">
        <Icon
          size={ComponentSize.MEDIUM}
          style={{ color: isBackgroundLight ? colorPrimary : '#ffffff' }}
          type={IconType.OUTLINED}
        >
          shopping_cart
        </Icon>
        <p>{translatedLabels.title}</p>
      </div>
      <div className="order-detail__reason">
        <p className="order-detail__reason-label">{translatedLabels.reason}:</p>
        <p className="order-detail__reason-value">{orderDetailName}</p>
      </div>
      <p className="top__monthly-installments" />
      <div className="order-detail__main-section">
        <div>
          <ul className="item-list">
            <li className="item-list__header">
              <span className="header__name">{translatedLabels.product}:</span>
              <span className="header__price">{translatedLabels.price}:</span>
            </li>
            {orderTemplate.lineItems.map(({ quantity, unitPrice, name }, index) => (
              <ProductRow
                key={`${name}-${index}`}
                name={name}
                quantity={quantity}
                quantityLabel={translatedLabels.quantity}
                unitPrice={parseCurrency(unitPrice)}
              />
            ))}
          </ul>
          {canShowAdditionalLines && (
            <AdditionalChargesSection
              parseCurrencyFunction={parseCurrency}
              orderTemplate={orderTemplate}
              labels={additionalChargesLabels}
            />
          )}
          <div className="order-detail__total-row">
            <TotalRow value={parseCurrency(amount)}>{translatedLabels.total}</TotalRow>
          </div>
        </div>
      </div>
    </div>
  );
};

export interface OrderDetailProps {
  name: string;
  amount: number;
  orderTemplate: OrderTemplate;
  hasSubscription?: boolean;
  planDetails?: any;
}

export default OrderDetail;
